// Định dạng số/tiền/diện tích khớp mobile-app-v2 (project.method.ts)

export const formatArea = (value) =>
  `${new Intl.NumberFormat("vi-VN").format(Number(value) || 0)} m²`;

export const formatPrice = (value) => {
  const v = Number(value) || 0;
  if (!v) return "0";
  if (v >= 1_000_000_000) {
    const billion = v / 1_000_000_000;
    return `${Number.isInteger(billion) ? billion : billion.toFixed(1)} tỷ`;
  }
  if (v >= 1_000_000) {
    const million = v / 1_000_000;
    return `${Number.isInteger(million) ? million : million.toFixed(1)} triệu`;
  }
  return new Intl.NumberFormat("vi-VN").format(v);
};

// Số tiền đầy đủ, ngăn cách bằng dấu chấm — khớp formatPrice của @/utils bên app
// (booking/locking/ttkq/hdmb hiển thị "5.042.891.070", không rút gọn tỷ/triệu).
export const formatMoney = (value) => {
  // + 0: chuẩn hoá -0 (làm tròn số âm rất nhỏ) về 0, tránh hiển thị "-0". Không đổi giá trị thực.
  const v = Math.round(Number(value) || 0) + 0;
  return new Intl.NumberFormat("de-DE").format(v);
};

export const formatTotalApartment = (value) => `${Number(value) || 0} căn`;

// Số tiền dạng "30,000,000" (ngăn cách phẩy en-US) — khớp formatMoneyToThousands của app (CCMG).
export const formatMoneyUS = (value) =>
  // + 0: chuẩn hoá -0 về 0, tránh hiển thị "-0".
  (Math.round(Number(value) || 0) + 0).toLocaleString("en-US");

// Số tiền CCMG hiển thị "30,000,000 VNĐ" — khớp BdcSummaryCard/BdcPeriodCard.
export const formatVnd = (value) => `${formatMoneyUS(value)} VNĐ`;

// Số tiền rút gọn dạng "10 tỷ 240 triệu" — khớp formatMoneyToString của app
// (dùng cho các thẻ thống kê ở màn Quản lý bán hàng / Quản lý thu nhập).
export const formatMoneyToString = (value) => {
  const money = Math.round(Number(value) || 0);
  if (money < 1e6) return `${money.toLocaleString("vi-VN")} đồng`;
  const billion = Math.floor(money / 1e9);
  const million = Math.floor((money % 1e9) / 1e6);
  let result = "";
  if (billion > 0) result += `${billion} tỷ`;
  if (million > 0) result += `${billion > 0 ? " " : ""}${million} triệu`;
  return result.trim();
};

// Tổng hợp diện tích / số căn / khoảng giá từ danh sách căn hộ
export const projectSummary = (apartments = []) => {
  const list = Array.isArray(apartments) ? apartments : [];
  if (!list.length) {
    return { totalApartment: "0 căn", totalArea: "0 m²", totalValueRange: "0 tỷ" };
  }
  const totalArea = list.reduce((s, a) => s + (Number(a?.area) || 0), 0);
  const totalValues = list
    .map((a) => Number(a?.totalValue) || 0)
    .filter((v) => v > 0);
  const min = totalValues.length ? Math.min(...totalValues) : 0;
  const max = totalValues.length ? Math.max(...totalValues) : 0;
  return {
    totalApartment: formatTotalApartment(list.length),
    totalArea: formatArea(totalArea),
    totalValueRange: min && max ? `${formatPrice(min)} - ${formatPrice(max)}` : "0",
  };
};

// Ghép địa chỉ khách hàng (khớp customer.method.buildCustomerAddress)
export const buildCustomerAddress = (data) => {
  if (!data) return "";
  const province = data?.permanentAddressProvinceData?.name || "";
  const district = data?.permanentAddressDistrictData?.name || "";
  const ward = data?.isNewDivision
    ? data?.permanentAddressWardNewData?.name || ""
    : data?.permanentAddressWardData?.name || "";
  const detail = data?.permanentAddress || "";
  return [detail, ward, district, province].filter(Boolean).join(", ");
};

// ===== Booking status (khớp BookingListCard) =====
export const BOOKING_STATUS_OPTIONS = [
  { value: "ALL", label: "Tất cả" },
  { value: "NEW", label: "Đang chờ duyệt" },
  { value: "APPROVED", label: "Đã duyệt" },
  { value: "REJECTED", label: "Từ chối" },
  { value: "REFUND", label: "Hoàn booking" },
  { value: "EDITING", label: "Yêu cầu chỉnh sửa" },
  { value: "PENDINGRETURNREQUEST", label: "Đang chờ hoàn booking" },
  { value: "REJECTRETURNREQUEST", label: "Từ chối hoàn booking" },
];

export const bookingStatusUi = (status) => {
  switch (status) {
    case "REFUND":
      return { label: "HOÀN BOOKING", bg: "#DDEDFF", color: "#1277EB" };
    case "REJECTRETURNREQUEST":
      return { label: "TỪ CHỐI HOÀN BOOKING", bg: "#FFF1F0", color: "#E5484D" };
    case "PENDINGRETURNREQUEST":
      return { label: "ĐANG CHỜ HOÀN BOOKING", bg: "#FFF7E8", color: "#F79D0D" };
    case "EDITING":
      return { label: "YÊU CẦU CHỈNH SỬA", bg: "#FFF7E8", color: "#F79D0D" };
    case "APPROVED":
      return { label: "ĐÃ DUYỆT", bg: "#E9FFF4", color: "#0BA859" };
    case "REJECTED":
      return { label: "TỪ CHỐI", bg: "#FFF1F0", color: "#E5484D" };
    default:
      return { label: "ĐANG CHỜ DUYỆT", bg: "#FFF7E8", color: "#F79D0D" };
  }
};

// ===== Locking status (khớp LockingListCard) =====
export const LOCKING_STATUS_OPTIONS = [
  { value: "ALL", label: "Tất cả" },
  { value: "NEW", label: "Đang chờ duyệt" },
  { value: "APPROVED", label: "Đã duyệt" },
  { value: "REJECTED", label: "Từ chối" },
  { value: "EDITING", label: "Yêu cầu chỉnh sửa" },
];

export const lockingStatusUi = (status) => {
  switch (status) {
    case "EDITING":
      return { label: "YÊU CẦU CHỈNH SỬA", bg: "#FFF7E8", color: "#F79D0D" };
    case "APPROVED":
      return { label: "ĐÃ DUYỆT", bg: "#E9FFF4", color: "#0BA859" };
    case "REJECTED":
      return { label: "TỪ CHỐI", bg: "#FFF1F0", color: "#E5484D" };
    default:
      return { label: "ĐANG CHỜ DUYỆT", bg: "#FFF7E8", color: "#F79D0D" };
  }
};

// ===== Income status (khớp Income*ListCard) =====
// src = item.locking (tạm tính) hoặc item (đối chiếu/chi trả)
export const incomeStatusUi = (src) => {
  const s = src || {};
  let label;
  if (s.status === "EDITING") label = "YÊU CẦU CHỈNH SỬA";
  else if (s.confirmCommissionStatus === "APPROVED") label = "ĐÃ ĐƯỢC ĐỐI CHIẾU";
  else if (s.confirmInformationStatus === "APPROVED") label = "HTBH ĐÃ DUYỆT";
  else if (s.registerRelativeStatus === "APPROVED") label = "HCNS ĐÃ DUYỆT";
  else if (s.status === "APPROVED") label = "ĐÃ DUYỆT";
  else if (s.status === "REJECTED") label = "TỪ CHỐI";
  else label = "ĐANG CHỜ DUYỆT";

  let bg = "#FFF7E8";
  let color = "#F79D0D";
  if (
    s.status === "APPROVED" ||
    s.confirmCommissionStatus === "APPROVED" ||
    s.confirmInformationStatus === "APPROVED" ||
    s.registerRelativeStatus === "APPROVED"
  ) {
    bg = "#E9FFF4";
    color = "#0BA859";
  }
  if (s.status === "REJECTED") {
    bg = "#FFF1F0";
    color = "#E5484D";
  }
  return { label, bg, color };
};

// HTML mô tả -> text thuần, giữ xuống dòng (khớp cách app hiển thị).
// Description trong DB bị mã hoá entity (&lt;br&gt; ...) nên decode trước.
export const htmlToPlain = (html = "", max) => {
  let s = String(html || "");
  // Decode entity 1-2 lớp
  for (let i = 0; i < 2; i++) {
    s = s
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&amp;/gi, "&")
      .replace(/&nbsp;/gi, " ")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/gi, '"');
  }
  s = s
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(p|div|li|h[1-6])\s*>/gi, "\n")
    .replace(/<[^>]+>/g, " ");
  s = s
    .split("\n")
    .map((l) => l.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  if (max && s.length > max) return `${s.slice(0, max).trim()}…`;
  return s;
};
